"use client";

import React, { Fragment, useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import Image from "@/helpers/Image";
import Link from "next/link";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { GROQCornerButton, IHeader, IMenu } from "@/sanity/lib/definitions";
import { CTACornerButton } from "@/shadcn/ui/corner-button";
import { resolvePageLink } from "@/sanity/globals/CTA";
import { useGlobalPathname } from "@/components/global/partials/HeaderContext";
import { forwardUTMParams } from "@/lib/utm";
import { usePostHog } from "posthog-js/react";
import {
  resolveI18nText,
  NON_DEFAULT_LANGUAGES,
  DEFAULT_LANGUAGE,
} from "@/sanity/globals/I18n";
import {
  Tooltip,
  TooltipCustomContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shadcn/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shadcn/ui/collapsible";
import { Button } from "@/shadcn/ui/button";
import { ChevronDown } from "lucide-react";
import DecorationLine from "@/components/global/ui/DecorationLine";
import PlatformUserCount from "@/components/global/ui/PlatformUserCount";
import SubCount from "@/components/global/youtube/SubCount";
import GlareHover from "@/components/global/ui/GlareHover";
import LanguageSwitcher from "./LanguageSwitcher";

import Logo from "@/public/global/new-logo-full.svg";
import LogoLight from "@/public/global/new-logo-full-light.svg";
import User from "@/public/global/User-avatar-filled-Black.svg";
import UserWhite from "@/public/global/User-avatar-filled-White.svg";
import IconMobileMenuClosed from "@/public/global/mobile-menu-btn.svg";
import IconMobileMenuClosedWhite from "@/public/global/mobile-menu-btn-light.svg";
import arrow1 from "@/public/global/arrow-bottom-right.svg";

const IconMobileMenuOpen = () => (
  <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15 32V30H17V32H15Z" fill="black" />
    <path d="M15 28L15 0H17L17 28H15Z" fill="black" />
    <path d="M0 15.001H2V17.001H0L0 15.001Z" fill="black" />
    <path d="M4 15.001L32 15.001V17.001H4V15.001Z" fill="black" />
  </svg>
);

const TooltipLink = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    className={cn("cursor-pointer select-none group", className)}
    ref={ref}
    {...props}
  />
));
TooltipLink.displayName = "TooltipLink";

function MenuItemPopover({
  item,
  language,
}: {
  item: IMenu;
  language: string;
}) {
  const pathname = usePathname();

  return (
    <div className="flex justify-center w-screen h-[48.5rem]">
      <div className="flex justify-between w-full h-full gap-[2rem] max-w-[225rem] pt-[3rem] pb-[5rem]">
        <div className="w-[min(118rem,100%)] h-full">
          <h5 className="uppercase !font-geistmono text-black/40 text-[2rem] font-[400]">
            Pillars
          </h5>
          <hr className="border-t border-black/10 mt-[2rem] mb-[3rem]" />
          <div className="grid grid-cols-2 gap-[2rem]">
            {item.subItems &&
              item.subItems.map((item, index) => {
                const link = resolvePageLink(item.link, language) ?? "/";
                const isActive = pathname === link;
                return (
                  <Link
                    key={index}
                    href={link}
                    target={item.link.externalLink ? "_blank" : "_self"}
                    className={cn(
                      "group",
                      isActive ? "pointer-events-none" : "",
                    )}
                    aria-current={isActive ? "page" : undefined}
                  >
                    <div
                      className={cn(
                        "flex items-start transition-all duration-200 gap-[1rem] p-[2rem] rounded-[.5rem]",
                        isActive ? "bg-[#EDECEB]" : "hover:bg-[#EDECEB]",
                      )}
                    >
                      {item.icon && (
                        <div className="overflow-hidden relative flex-shrink-0 w-[4rem] h-[4rem]">
                          <Image
                            src={item.icon}
                            alt=""
                            className="opacity-100 transition-all duration-300 group-hover:opacity-0 w-[4rem] h-[4rem] group-hover:translate-x-[2.25rem] group-hover:-translate-y-[2.25rem]"
                          />
                          <Image
                            src={item.icon}
                            alt=""
                            className="absolute opacity-0 transition-all duration-300 pointer-events-none group-hover:opacity-100 w-[4rem] h-[4rem] top-[2.25rem] -left-[2.25rem] group-hover:translate-x-[2.25rem] group-hover:-translate-y-[2.25rem]"
                          />
                        </div>
                      )}
                      <div className="flex flex-col gap-[1rem]">
                        <div className="overflow-hidden relative h-[3.25rem]">
                          <span className="block m-0 uppercase opacity-100 transition-all duration-300 group-hover:opacity-0 text-black/80 font-[500] text-[2.5rem] font-geistmono group-hover:-translate-y-[3rem]">
                            {resolveI18nText(
                              item.title,
                              item.titleI18n,
                              language,
                            )}
                          </span>
                          <span className="block absolute m-0 uppercase opacity-0 transition-all duration-300 pointer-events-none group-hover:opacity-100 top-[3rem] text-black/80 font-[500] text-[2.5rem] font-geistmono group-hover:-translate-y-[3rem]">
                            {resolveI18nText(
                              item.title,
                              item.titleI18n,
                              language,
                            )}
                          </span>
                        </div>
                        <span className="text-[2rem] font-normal leading-[130%] transition-all duration-200 text-black/30 group-hover:text-black/70">
                          {resolveI18nText(
                            item.description,
                            item.descriptionI18n,
                            language,
                          )}
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
          </div>
        </div>
        <div className="relative h-full w-[min(102rem,100%)]">
          <h5 className="uppercase !font-geistmono text-black/40 text-[2rem] font-[400]">
            Socials
          </h5>
          <hr className="border-t border-black/10 mt-[2rem] mb-[3rem]" />
          <div className="grid grid-cols-2 w-full gap-[2rem]">
            {item.socialSubItems &&
              item.socialSubItems.map((item, index) => {
                const link = resolvePageLink(item.link, language) ?? "/";
                const isActive = pathname === link;

                return (
                  <Link href={link} key={index} className="flex gap-[2rem]">
                    {item.image && (
                      <GlareHover className="!w-[26rem] !h-[26rem] flex-shrink-0">
                        <Image
                          src={item.image}
                          alt=""
                          className="object-cover !w-[26rem] !h-[26rem]"
                        />
                      </GlareHover>
                    )}

                    <div className="flex flex-col justify-between w-full px-[2rem] py-[3rem]">
                      <div className="flex flex-col gap-[2rem]">
                        <p className="m-0 uppercase text-black/80 font-[500] text-[2.5rem] font-geistmono">
                          {item.title}
                        </p>
                        <div className="flex mb-0 gap-[.325em] text-[2rem]">
                          {item.title === "discord" ? (
                            <>
                              <PlatformUserCount
                                displayMode="full"
                                className="text-black/40"
                              />
                              <span className="text-black/40">Members</span>
                            </>
                          ) : (
                            <SubCount
                              handle="@Bitbulldaily"
                              className="text-black/40"
                              language={language === "en" ? "en" : "de"}
                            />
                          )}
                        </div>
                      </div>
                      <div className="flex justify-between items-center !w-full group  relative">
                        <div className="absolute right-0 bottom-0 left-0 w-0 bg-black transition-all duration-300 group-hover:w-full h-[.150rem]" />
                        <div className="overflow-hidden relative h-[3.375rem]">
                          <h5 className="text-initial font-[400] text-[2.25rem] leading-[150%] text-black transition-all duration-300 opacity-100 group-hover:opacity-0 group-hover:-translate-y-[3rem]">
                            {language === "de" ? "Jetzt beitreten" : "Join now"}
                          </h5>
                          <h5 className="absolute top-[3rem] font-[400] text-[2.25rem] leading-[150%] text-black transition-all duration-300 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:-translate-y-[3rem]">
                            {language === "de" ? "Jetzt beitreten" : "Join now"}
                          </h5>
                        </div>
                        <div className="overflow-hidden relative w-[3rem] h-[3rem]">
                          <Image
                            src={arrow1}
                            alt=""
                            className="opacity-100 transition-all duration-300 group-hover:opacity-0 group-hover:translate-x-[2.25rem] group-hover:-translate-y-[2.25rem]"
                          />
                          <Image
                            src={arrow1}
                            alt=""
                            className="absolute opacity-0 transition-all duration-300 -rotate-45 pointer-events-none group-hover:opacity-100 top-[2.25rem] -left-[2.25rem] group-hover:translate-x-[2.25rem] group-hover:-translate-y-[2.25rem]"
                          />
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
}

function MenuItem({
  item,
  headerStyle,
  onOpenChange,
  language,
}: {
  item: IMenu;
  headerStyle: "light" | "dark";
  onOpenChange?: (open: boolean) => void;
  language: string;
}) {
  const pathnames = useGlobalPathname();
  const link = resolvePageLink(item.link ?? null, language) ?? "/";
  const isActive = pathnames.currentPathname === link && !item.isCategory;
  const LinkWrapper = item.isCategory ? TooltipLink : Link;
  const [isOpen, setIsOpen] = React.useState(false);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);
  };

  const handleClick = (e: React.MouseEvent | React.TouchEvent) => {
    if (item.isCategory) {
      e.preventDefault();
      handleOpenChange(!isOpen);
    }
  };

  const handleMouseEnter = () => {
    if (!item.isCategory) {
      // Close any open dropdowns when hovering over non-category items
      onOpenChange?.(false);
    }
  };

  return (
    <TooltipProvider delayDuration={100} skipDelayDuration={60}>
      <Tooltip open={isOpen} onOpenChange={handleOpenChange}>
        <TooltipTrigger asChild>
          <LinkWrapper
            href={link}
            onClick={handleClick}
            onTouchEnd={handleClick}
            onMouseEnter={handleMouseEnter}
            className={cn(
              "group px-8 text-[2rem] leading-none inline-flex items-center transition-colors font-geistmono uppercase relative data-[state=delayed-open]:opacity-100",
              headerStyle === "dark"
                ? isActive
                  ? "text-black/80"
                  : "text-black/40 hover:text-black/80"
                : isActive
                  ? "text-white/80"
                  : "text-white/40 hover:text-white/60",
            )}
          >
            <>
              {resolveI18nText(item.title, item.titleI18n, language)}

              {item.isCategory && (
                <ChevronDown
                  className={cn(
                    "ml-3 !w-[1.25rem] transition-transform",
                    isOpen && "rotate-180",
                    isActive ? "opacity-100" : "opacity-80",
                  )}
                />
              )}

              <span
                className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-[.125rem] w-full ${headerStyle === "dark" ? "bg-black" : "bg-[#D9F9B9]"} group-hover:opacity-100 transition-opacity ${isActive ? "opacity-100" : "opacity-0"}`}
              />
            </>
          </LinkWrapper>
        </TooltipTrigger>
        {item?.isCategory && item?.subItems && item.subItems?.length > 0 && (
          <TooltipCustomContent
            className="left-0 right-0 !translate-x-0 bg-[#F3F2F1] border-t border-black/10"
            align="start"
            sideOffset={0}
            alignOffset={-9999}
            onPointerDownOutside={() => handleOpenChange(false)}
          >
            <MenuItemPopover item={item} language={language} />
          </TooltipCustomContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}

export default function HeaderV2({
  data,
  headerType,
  headerStyle = "dark",
  headerLines = "dark",
  lpCTA,
  className,
}: {
  data: IHeader;
  headerType: "main" | "lp" | "hidden";
  headerStyle?: "light" | "dark";
  headerLines?: "light" | "medium" | "dark";
  lpCTA?: GROQCornerButton;
  className?: string;
}) {
  const pathnames = useGlobalPathname();
  const menuItemsContainer = useRef<HTMLDivElement>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileMenuContainer, setMobileMenuContainer] =
    useState<HTMLElement | null>(null);

  const [loginUrl, setLoginUrl] = useState("https://trademania.app/login");
  const [signUrl, setSignUrl] = useState("https://trademania.app/register");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const posthog = usePostHog();

  // Compute active styles based on dropdown state
  const activeHeaderStyle =
    isDropdownOpen && headerStyle === "light" ? "dark" : headerStyle;
  const activeHeaderLines =
    isDropdownOpen && headerLines === "light" ? "dark" : headerLines;
  const shouldShowLines = isDropdownOpen;

  useEffect(() => {
    // Forward UTM parameters to login URL
    const urlWithUTMs = forwardUTMParams("https://trademania.app/login");
    setLoginUrl(urlWithUTMs);

    const signupUrlWithUTMs = forwardUTMParams(
      "https://trademania.app/register",
    );
    setSignUrl(signupUrlWithUTMs);
  }, []);

  useEffect(() => {
    setIsMounted(true);
    if (typeof document !== "undefined") {
      const portal = document.createElement("div");
      portal.id = "mobile-menu";
      portal.style.position = "fixed";
      portal.style.top = "0";
      portal.style.left = "0";
      portal.style.width = "100%";
      portal.style.height = "100%";
      portal.style.zIndex = "100";
      portal.style.transform = "translateX(100%)";
      document.body.appendChild(portal);
      setMobileMenuContainer(portal);
    }

    return () => {
      setIsMounted(false);
      const portal = document.getElementById("mobile-menu");
      if (portal) {
        portal.remove();
      }
      setMobileMenuContainer(null);
    };
  }, []);

  const language =
    NON_DEFAULT_LANGUAGES.find(
      (l) =>
        pathnames.currentPathname.startsWith(`/${l}/`) ||
        pathnames.currentPathname === `/${l}`,
    ) ?? DEFAULT_LANGUAGE;

  if (!data) {
    return null;
  }
  return (
    <>
      {/* Backdrop blur overlay */}
      <div
        className={cn(
          "fixed inset-0 z-40 transition-all duration-150 top-[8rem]",
          isDropdownOpen
            ? "backdrop-blur-sm bg-black/20 opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none",
        )}
        onClick={() => setIsDropdownOpen(false)}
      />

      <motion.header
        initial={{ y: "calc(-100% - 1.5rem)" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={cn(
          "w-full relative lg:px-[7rem] z-50 transition-colors duration-150",
          className,
          isDropdownOpen && "!bg-[#F3F2F1]",
        )}
      >
        <div
          className={`headerContainer w-full max-w-[225rem] h-[7rem] mx-auto px-[2rem] lg:px-0 flex items-center ${headerType === "main" ? "justify-between" : "justify-center"} `}
        >
          {headerType === "main" && (
            <>
              <div className="flex items-center w-full lg:justify-start">
                <Link href={language === "en" ? "/en" : "/"}>
                  <Image
                    className="!w-[16.15rem]"
                    src={activeHeaderStyle === "dark" ? Logo : LogoLight}
                    alt="Header Logo"
                    priority={true}
                    fetchPriority="high"
                  />
                </Link>
              </div>

              <div
                className="menu-items-container hidden relative flex-shrink-0 justify-start h-full lg:flex lg:justify-center w-[99rem]"
                ref={menuItemsContainer}
              >
                <DecorationLine
                  className={cn(
                    `headerVerticalLine left-0 transition-opacity duration-150`,
                    headerLines === "medium"
                      ? "border-l-[#202223]/[.15] border-t-[#202223]/[.15] border-r-white border-b-white opacity-[.15]"
                      : "",
                    shouldShowLines ? "!opacity-100" : "",
                  )}
                  orientation="vertical"
                  style={activeHeaderLines === "dark" ? "dark" : "light"}
                />
                <DecorationLine
                  className={cn(
                    `headerVerticalLine right-0 transition-opacity duration-150`,
                    headerLines === "medium"
                      ? "border-l-[#202223]/[.15] border-t-[#202223]/[.15] border-r-white border-b-white opacity-[.15]"
                      : "",
                    shouldShowLines ? "!opacity-100" : "",
                  )}
                  orientation="vertical"
                  style={activeHeaderLines === "dark" ? "dark" : "light"}
                />

                {data.menu &&
                  data.menu.map((item, index) => (
                    <MenuItem
                      item={item}
                      headerStyle={activeHeaderStyle}
                      key={index}
                      onOpenChange={(open) => {
                        if (item.isCategory) {
                          setIsDropdownOpen(open);
                        }
                      }}
                      language={language}
                    />
                  ))}
              </div>

              <div className="flex justify-end lg:justify-between w-full min-w-[25rem] h-full gap-[1rem] lg:gap-[2rem]">
              {/* <div className="flex justify-end w-full h-full gap-[1rem] lg:gap-[2rem]"> */}
                <div className="flex gap-[2rem] h-full items-center justify-end ps-[1.25rem] pe-[2rem] relative">
                  <div className="flex gap-2 items-center">
                    {/* <div className="hidden lg:flex gap-2 items-center"> */}
                    <Image
                      src={activeHeaderStyle === "dark" ? User : UserWhite}
                      alt="users icon"
                      className="!w-[4rem]"
                    />
                    <div
                      className={
                        activeHeaderStyle === "dark"
                          ? "prose-p:text-black/30"
                          : "prose-p:text-white/40"
                      }
                    >
                      <PlatformUserCount displayMode="short" />
                    </div>
                  </div>
                  <LanguageSwitcher language={language} />

                  <DecorationLine
                    className={cn(
                      `hidden lg:block headerVerticalLine right-0 transition-opacity duration-150`,
                      headerLines === "medium"
                        ? "border-l-[#202223]/[.15] border-t-[#202223]/[.15] border-r-white border-b-white opacity-[.15]"
                        : "",
                      shouldShowLines ? "!opacity-100" : "",
                    )}
                    orientation="vertical"
                    style={activeHeaderLines === "dark" ? "dark" : "light"}
                  />
                </div>

                <div className="flex gap-4 items-center justify-end">
                  <a
                    href={signUrl}
                    className="hidden lg:inline-block"
                    onClick={() => {
                      posthog?.capture("clicked_signup_button", {
                        source: "header_desktop",
                        destination_url: signUrl,
                        affiliate:
                          localStorage.getItem("affiliate") || undefined,
                      });
                    }}
                  >
                    <Button
                      variant={
                        activeHeaderStyle === "dark" ? "borders" : "transparent"
                      }
                      shape={"round"}
                      size={"default"}
                      className="h-[5rem] px-[2rem]"
                    >
                      {language === "de" ? "REGISTRIEREN" : "SIGN UP"}
                    </Button>
                  </a>
                  <a
                    href={loginUrl}
                    className="hidden lg:inline-block"
                    onClick={() => {
                      posthog?.capture("clicked_login_button", {
                        source: "header_desktop",
                        destination_url: loginUrl,
                        affiliate:
                          localStorage.getItem("affiliate") || undefined,
                      });
                    }}
                  >
                    <Button
                      variant={
                        activeHeaderStyle === "dark"
                          ? "darkGreen"
                          : "lightGreen"
                      }
                      shape={"round"}
                      size={"default"}
                      className="h-[4.875rem] px-[3rem]"
                    >
                      {language === "de" ? "EINLOGGEN" : "LOG IN"}
                    </Button>
                  </a>
                </div>
              </div>

              <button
                className={cn(
                  "lg:hidden flex-shrink-0 relative",
                  headerStyle === "dark"
                    ? "w-[6.5rem] h-[5rem] flex items-center justify-center rounded-[8px] border-[2px] border-white after:absolute after:-top-[1px] after:-left-[1px] after:-right-[1px] after:-bottom-[1px] after:border after:border-[#D3D3D2] after:rounded-[inherit] after:pointer-events-none"
                    : "",
                )}
                onClick={() => {
                  setIsMobileMenuOpen(true);
                }}
              >
                <Image
                  src={
                    headerStyle === "dark"
                      ? IconMobileMenuClosed
                      : IconMobileMenuClosedWhite
                  }
                  alt="Mobile menu button"
                  className="!w-[4rem]"
                />
              </button>
            </>
          )}

          {headerType === "lp" && (
            <>
              <div
                className={cn(
                  "flex justify-center items-center w-full",
                  lpCTA && "lg:justify-between",
                )}
              >
                <Link href={language === "en" ? "/en" : "/"}>
                  <Image
                    className="!w-[16.15rem]"
                    src={headerStyle === "dark" ? Logo : LogoLight}
                    alt="Header Logo"
                    priority={true}
                    fetchPriority="high"
                  />
                </Link>

                {/* optional CTA on the right (desktop only, logo stays centered on mobile) */}
                {lpCTA && (
                  <CTACornerButton
                    className="hidden lg:inline-block !w-auto !min-w-0 shrink-0 [&_.corner-button]:min-h-[6.5rem] [&_.corner-button]:text-[2rem] [&_.corner-button]:gap-[1.5rem] [&_.corner-button]:px-[3.5rem] [&_.corner-button-divider]:h-[3rem]"
                    data={lpCTA}
                  />
                )}
              </div>
            </>
          )}
        </div>

        {mobileMenuContainer &&
          createPortal(
            <>
              <div
                className={`fixed lg:hidden z-[80] w-full h-full pb-[4rem] transition-all duration-300 ease-out bg-[#F3F2F1] overflow-y-auto ${isMobileMenuOpen ? "-translate-x-full" : "translate-x-0"}`}
              >
                <div className="container flex flex-row justify-between items-center mb-[6rem] pt-[1rem]">
                  <Image
                    src={Logo}
                    alt="Header Logo"
                    className="!w-[16.15rem]"
                  />

                  <button
                    className="w-[6.5rem] h-[5rem] relative flex items-center justify-center rounded-[8px] border-[2px] border-white after:absolute after:-top-[1px] after:-left-[1px] after:-right-[1px] after:-bottom-[1px] after:border after:border-[#D3D3D2] after:rounded-[inherit] after:pointer-events-none"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    <div className="w-[4rem] h-[4rem] rotate-45">
                      <IconMobileMenuOpen />
                    </div>
                  </button>
                </div>

                <div className="container flex flex-col justify-start items-stretch">
                  {data.menu &&
                    data.menu.map((item, index) => {
                      const link =
                        resolvePageLink(item.link ?? null, language) ?? "/";
                      const isActive = pathnames.currentPathname === link;
                      const isCategoryActive =
                        item.isCategory &&
                        item.subItems?.some((subItem) => {
                          const subLink =
                            resolvePageLink(subItem?.link, language) ?? "/";
                          return pathnames.currentPathname === subLink;
                        });
                      const LinkWrapper = item.isCategory ? TooltipLink : Link;
                      const isLastItem = index === data.menu.length - 1;

                      return (
                        <React.Fragment key={index}>
                          {item.isCategory ? (
                            <Collapsible
                              className="w-full transition-transform duration-150 group"
                              defaultOpen={
                                isCategoryActive &&
                                item?.isCategory &&
                                item?.subItems &&
                                item?.subItems?.length > 0
                              }
                            >
                              <CollapsibleTrigger asChild>
                                <LinkWrapper
                                  href={link}
                                  className="w-full py-[3rem] text-[3rem] leading-none flex justify-between items-center border-b border-black border-opacity-10 last:border-0 group-data-[state=open]:border-opacity-0"
                                >
                                  <>
                                    <span
                                      className={`text-[2.25rem] leading-none font-[400] font-geistmono uppercase ${isActive ? "text-black/80" : "text-black/40"}`}
                                    >
                                      {resolveI18nText(
                                        item.title,
                                        item.titleI18n,
                                        language,
                                      )}
                                    </span>
                                    {item.isCategory && (
                                      <ChevronDown
                                        className={cn(
                                          "ml-[1rem] !w-[2rem] shrink-0 transition-transform group-data-[state=open]:rotate-180",
                                          isActive
                                            ? "text-black/80"
                                            : "text-black/40",
                                        )}
                                      />
                                    )}
                                  </>
                                </LinkWrapper>
                              </CollapsibleTrigger>

                              {item?.isCategory &&
                                item?.subItems &&
                                item.subItems?.length > 0 && (
                                  <CollapsibleContent className="relative w-full  data-[state=open]:animate-in data-[state=closed]:animate-out">
                                    <div className="flex flex-col justify-start items-stretch gap-[1rem] pt-[.25rem] ps-[2.5rem]">
                                      <h5 className="mb-[1rem] text-[1.75rem] font-geistmono uppercase leading-[100%] text-black/30">
                                        Pillars
                                      </h5>
                                      <hr className="w-full border-t border-black/10" />
                                      {item.subItems?.map((subItem, index) => {
                                        const link =
                                          resolvePageLink(
                                            subItem?.link,
                                            language,
                                          ) ?? "/";
                                        const isActive =
                                          pathnames.currentPathname === link;
                                        const isLastSubmenuItem =
                                          index ===
                                          (item.subItems?.length ?? 0) - 1;
                                        return (
                                          <Fragment key={`submenu-${index}`}>
                                            <Link
                                              key={`submenu-${index}`}
                                              href={link}
                                              aria-current={
                                                isActive ? "page" : undefined
                                              }
                                              className={cn(
                                                "flex items-start gap-[1rem] rounded-[.5rem] p-[1rem] transition-colors",
                                                isActive
                                                  ? "bg-[#EDECEB] pointer-events-none"
                                                  : "hover:bg-[#EDECEB]",
                                              )}
                                            >
                                              {subItem.icon && (
                                                <Image
                                                  src={subItem.icon}
                                                  alt=""
                                                  className="flex-shrink-0 w-[4rem]"
                                                />
                                              )}
                                              <div className="flex flex-col gap-[1rem]">
                                                <span
                                                  className={`uppercase text-[2rem] text-black/80 font-[400] font-geistmono ${isActive ? "text-black/80" : "text-black/40"}`}
                                                >
                                                  {resolveI18nText(
                                                    subItem.title,
                                                    subItem.titleI18n,
                                                    language,
                                                  )}
                                                </span>
                                                <p className="text-black/80 text-[2rem] leading-[130%]">
                                                  {resolveI18nText(
                                                    subItem.description,
                                                    subItem.descriptionI18n,
                                                    language,
                                                  )}
                                                </p>
                                              </div>
                                            </Link>
                                            {!isLastSubmenuItem && (
                                              <hr className="w-full border-t border-black/10" />
                                            )}
                                          </Fragment>
                                        );
                                      })}
                                    </div>

                                    <div className="flex flex-col justify-start items-stretch gap-[2.75rem] pt-[4rem] ps-[2.5rem]">
                                      <h5 className="text-[1.75rem] font-geistmono uppercase leading-[100%] text-black/30">
                                        social
                                      </h5>
                                      <hr className="w-full border-t border-black/10" />
                                      {item.socialSubItems?.map(
                                        (socialItem, index) => {
                                          const link =
                                            resolvePageLink(
                                              socialItem?.link,
                                              language,
                                            ) ?? "/";
                                          const isActive =
                                            pathnames.currentPathname === link;
                                          const isLastSubmenuItem =
                                            index ===
                                            (item.socialSubItems?.length ?? 0) -
                                              1;
                                          return (
                                            <Fragment key={`submenu-${index}`}>
                                              <Link
                                                key={`submenu-${index}`}
                                                href={link}
                                                className={` flex items-stretch gap-[1rem]    ${isLastSubmenuItem && !isLastItem ? "border-b border-black border-opacity-10 pb-[3rem]" : ""}`}
                                              >
                                                {socialItem.image && (
                                                  <Image
                                                    src={socialItem.image}
                                                    alt=""
                                                    className="object-cover flex-shrink-0 !w-[16.75rem] aspect-square"
                                                  />
                                                )}

                                                <div className="flex flex-col justify-between w-full p-[1rem]">
                                                  <div className="flex flex-col">
                                                    <p className="m-0 uppercase text-black/80 font-[500] text-[2.5rem] font-geistmono">
                                                      {socialItem.title}
                                                    </p>
                                                    <div className="flex mb-0 gap-[.325em] text-[2rem]">
                                                      {socialItem.title ===
                                                      "discord" ? (
                                                        <>
                                                          <PlatformUserCount
                                                            displayMode="full"
                                                            className="text-black/40"
                                                          />
                                                          <span className="text-black/40">
                                                            Members
                                                          </span>
                                                        </>
                                                      ) : (
                                                        <SubCount
                                                          handle="@Bitbulldaily"
                                                          className="text-black/40"
                                                          language={language === "en" ? "en" : "de"}
                                                        />
                                                      )}
                                                    </div>
                                                  </div>

                                                  <div className="flex justify-between items-center !w-full">
                                                    <h5 className="font-[400] text-[2.25rem] leading-[150%] text-black">
                                                      {language === "de"
                                                        ? "Jetzt beitreten"
                                                        : "Join now"}
                                                    </h5>
                                                    <Image
                                                      src={arrow1}
                                                      alt=""
                                                      className=""
                                                    />
                                                  </div>
                                                </div>
                                              </Link>
                                            </Fragment>
                                          );
                                        },
                                      )}
                                    </div>
                                  </CollapsibleContent>
                                )}
                            </Collapsible>
                          ) : (
                            <Link
                              className={`w-full py-[3rem] text-[3rem] leading-none flex justify-between items-center border-b border-black border-opacity-10 last:border-0 group-data-[state=open]:border-0`}
                              href={link ?? "/"}
                              target={
                                item.link?.externalLink ? "_blank" : "_self"
                              }
                            >
                              <div className="flex justify-between items-center w-full">
                                <span
                                  className={`text-[2.25rem] leading-none font-[400] font-geistmono uppercase ${isActive ? "text-black/80" : "text-black/40"}`}
                                >
                                  {resolveI18nText(
                                    item.title,
                                    item.titleI18n,
                                    language,
                                  )}
                                </span>
                              </div>
                            </Link>
                          )}
                        </React.Fragment>
                      );
                    })}
                </div>

                <div className="container flex flex-col w-full gap-[1rem] mt-[4rem]">
                  <a
                    href={signUrl}
                    className="w-full"
                    onClick={() => {
                      posthog?.capture("clicked_signup_button", {
                        source: "header_mobile",
                        destination_url: signUrl,
                        affiliate:
                          localStorage.getItem("affiliate") || undefined,
                      });
                    }}
                  >
                    <Button
                      variant={"white"}
                      shape={"round"}
                      size={"default"}
                      className="h-[6rem]"
                    >
                      {language === "de" ? "REGISTRIEREN" : "SIGN UP"}
                    </Button>
                  </a>
                  <a
                    href={loginUrl}
                    className="w-full"
                    onClick={() => {
                      posthog?.capture("clicked_login_button", {
                        source: "header_mobile",
                        destination_url: loginUrl,
                        affiliate:
                          localStorage.getItem("affiliate") || undefined,
                      });
                    }}
                  >
                    <Button
                      variant={"darkGreen"}
                      shape={"round"}
                      size={"default"}
                      className="h-[6rem]"
                    >
                      {language === "de" ? "EINLOGGEN" : "LOG IN"}
                    </Button>
                  </a>
                </div>
              </div>
            </>,
            mobileMenuContainer,
            "mobile-menu",
          )}

        {headerType !== "lp" && (
          <DecorationLine
            className={cn(
              `headerHorizontalLine top-full left-0 transition-opacity duration-150`,
              headerLines === "medium"
                ? "border-l-[#202223]/[.15] border-t-[#202223]/[.15] border-r-white border-b-white opacity-[.15]"
                : "",
              shouldShowLines ? "!opacity-100" : "",
            )}
            orientation="horizontal"
            style={activeHeaderLines === "dark" ? "dark" : "light"}
          />
        )}
      </motion.header>
    </>
  );
}
